import { CountryListDropdown, Icon } from '@aibees/shared/components';
import { useAddTaxIdMutation } from '@aibees/shared/features/billing-profile/api/use-add-tax-id-mutation';
import { useBrowseTaxIdApi } from '@aibees/shared/features/billing-profile/api/use-browse-tax-id-api';
import { useEditCustomerMutationApi } from '@aibees/shared/features/billing-profile/api/use-edit-customer-mutation-api';
import { useReadCustomerApi } from '@aibees/shared/features/billing-profile/api/use-read-customer-api';
import { TaxIdForm } from '@aibees/shared/features/billing-profile/components/tax-id-form';
import { useNotify } from '@aibees/shared/hooks';
import { classnames } from '@aibees/shared/utils';
import { YupFormData } from '@aibees/shared/utils/yup';
import { useWorkspace } from '@aibees/shared/workspace';
import { Button, Grid, Stack, Typography } from '@mui/material';
import { HttpStatusCode } from 'axios';
import { random } from 'lodash-es';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { BillingInformationInput } from './billing-information-input';
import { config, scheme } from './config';
import * as Styled from './styles';
import * as SX from './sx';
import type { BillingAddressHeaderProps as Props } from './types';
import { useFormChangeWatcher } from './use-form-change-watcher';

export const BillingAddressHeader = memo(({ className }: Props) => {
  const notify = useNotify();
  const workspaceId = useWorkspace()?.id;
  const addTaxIdApi = useAddTaxIdMutation();
  const { data: existingTaxIds = [], refetch: taxIdRefetch } =
    useBrowseTaxIdApi();
  const { data: item } = useReadCustomerApi(workspaceId || '');
  const api = useEditCustomerMutationApi(workspaceId || '');
  const [formChanged, setFormChanged] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const [globalTaxIdError, setGlobalTaxIdError] = useState<string | null>(null);

  const handleDeleteTaxId = async (_taxIdDbId: string) => {
    await taxIdRefetch();
  };

  const defaultValues = useMemo(
    () => ({
      ...item,
      taxIds: existingTaxIds.map((id) => ({
        id: id.id,
        type: id.type,
        value: id.value,
        existing: true,
      })),
    }),
    [item, existingTaxIds]
  );

  useEffect(() => {
    if (item && existingTaxIds.length > 0) {
      setFormKey((prev) => prev + 1);
    }
  }, [item, existingTaxIds.length]);

  const onSubmitBase = (data: YupFormData<typeof scheme>) => {
    if (!item) return;

    setGlobalTaxIdError(null);

    api
      .mutateAsync({
        ...item,
        name: data.name,
        address: data.address,
      })
      .then(() => {
        const taxIds = data.taxIds ?? [];

        const newValidTaxIds = taxIds.filter(
          (taxId) =>
            !taxId.existing && taxId.type?.trim() && taxId.value?.trim()
        );

        if (newValidTaxIds.length === 0) return;

        return Promise.all(
          newValidTaxIds.map((taxId) =>
            addTaxIdApi.mutateAsync({
              type: taxId.type,
              value: taxId.value,
            })
          )
        );
      })
      .then(() => {
        notify.complete(
          'Billing address Updated',
          `Billing Address Updated successfully.`,
          random(1, 101),
          {
            icon: 'location',
          }
        );
        taxIdRefetch().then(() => {
          setFormKey((prev) => prev + 1);
        });
        setFormChanged(false);
      })
      .catch((error) => {
        const backendMessage =
          'Your Tax ID is invalid. Please check the format and try again.';

        if (error.status === HttpStatusCode.BadRequest) {
          setGlobalTaxIdError(backendMessage);
        } else {
          notify.error(
            'There was an error saving the changes. Please try again.'
          );
        }
      });
  };

  const FormChangeWatcher = ({ onChange }: { onChange: () => void }) => {
    useFormChangeWatcher(onChange);
    return null;
  };

  const handleDialogClose = useCallback(() => {
    setGlobalTaxIdError(null);
    setFormChanged(false);
    setFormKey((prev) => prev + 1);
  }, []);

  return (
    <Styled.Wrapper
      className={classnames('BillingAddressHeader', className)}
      sx={SX.root}
    >
      <Stack direction="row" gap={0.5}>
        <Icon name="location" size={20} color="grey.700" />
        <Stack>
          <Typography variant="body4" fontWeight={500} color="grey.600">
            Billing Address
          </Typography>
          <Typography variant="body5" fontWeight={400} color="grey.500">
            Your billing address information
          </Typography>
        </Stack>
      </Stack>
      <Styled.Dialog
        uncontrolledElement={
          <Button variant="outlined" size="xsmall">
            Edit Information
          </Button>
        }
        key={formKey}
        title={config.title}
        icon={config.icon}
        scheme={config.scheme}
        defaultValues={defaultValues}
        onSubmit={onSubmitBase}
        submitButtonProps={config.submitButtonProps}
        disabled={!formChanged}
        disableOnInvalid={false}
        onClose={handleDialogClose}
        maxWidth="md"
      >
        <FormChangeWatcher onChange={() => setFormChanged(true)} />

        <Grid container spacing={1}>
          <Grid item xs={12} md={6}>
            <Stack gap={1}>
              <BillingInformationInput name="name" label="Company Name" />
              <BillingInformationInput
                name="address.line1"
                label="Address Line 1"
              />
              <BillingInformationInput
                name="address.line2"
                label="Address Line 2"
              />
            </Stack>
          </Grid>
          <Grid item xs={12} md={6}>
            <Stack gap={1}>
              <BillingInformationInput name="address.city" label="City" />
              <BillingInformationInput
                name="address.country"
                label="Country"
                element={CountryListDropdown}
                extra={{ noLabel: true }}
              />
              <BillingInformationInput
                name="address.postalCode"
                label="Postal Code"
                extra={{
                  inputProps: {
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                  },
                  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                    const allowedKeys = [
                      'Backspace',
                      'Delete',
                      'ArrowLeft',
                      'ArrowRight',
                      'Tab',
                    ];
                    if (
                      !/^[0-9]$/.test(e.key) &&
                      !allowedKeys.includes(e.key)
                    ) {
                      e.preventDefault();
                    }
                  },
                }}
              />
            </Stack>
          </Grid>
        </Grid>

        <TaxIdForm
          onChange={(shouldEnable = true) => setFormChanged(shouldEnable)}
          onRefetch={() => {
            taxIdRefetch().then(() => {
              setFormKey((prev) => prev + 1);
            });
          }}
          onDeleteTaxId={handleDeleteTaxId}
          globalTaxIdError={globalTaxIdError}
          onClearError={() => setGlobalTaxIdError(null)}
        />
      </Styled.Dialog>
    </Styled.Wrapper>
  );
});

BillingAddressHeader.displayName = 'BillingAddressHeader';
